import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Slice } from 'gatsby';
import * as JsSearch from 'js-search';
import { isMobile } from 'react-device-detect';
import _get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import App from '../src/App';
import PageContextProvider from '../src/context';
import BlogListItem from '../src/views/Content/blog/BlogListItem';
import SEO from '../src/components/SEO';
import Header from '../src/views/Header';
import Footer from '../src/views/Footer';
import CategoryList from '../src/views/Content/blog/CategoryList';
import BlogAuthorList from '../src/views/Content/blog/BlogAuthorList';
import PopularTags from '../src/views/Content/blog/PopularTags';
import translate from '../src/utils/translations';
import pageContextType from '../src/types/pageContextType';
import { prefixPath } from '../src/utils/prefixPath';
import BackNavigationLink from '../src/components/BreadCrumbs/BackNavigationLink';
import '../src/sass/src/pages/index.scss';
require('../src/graphql/fragments/BlogContent');
require('../src/graphql/fragments/PageContent');
require('../src/graphql/fragments/BlogContent');
const listedAuthorsCount = 4;
const minimumRelatedBlog = 3;
const shownTagsCount = 20;
const searchOptions = {
  selectedStrategy: isMobile ? 'Prefix match' : 'All',
  itemPerPage: 5
};
const BlogSearch = ({
  data,
  location,
  pageContext
}) => {
  const {
    langcode,
    site = 'hu'
  } = pageContext;
  const {
    blog,
    blogCategories,
    blogAuthors,
    blogTags
  } = data;
  const search = _get(location, 'state.searchTerm', '');
  const [searchTerm, setSearchTerm] = useState(search);
  const [searchResults, setSearchResults] = useState([]);
  const [searchEngine, setSearchEngine] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [isLastPage, setIsLastPage] = useState();
  const [totalPages, setTotalPages] = useState();
  const pageData = {
    mainMenu: pageContext.mainMenu,
    footerMenu: pageContext.footerMenu,
    otherLangPath: '/blog/search'
  };
  const listedUsers = blogAuthors.group.sort((a, b) => a.totalCount <= b.totalCount ? 1 : -1).slice(0, listedAuthorsCount);
  const tags = [];
  blogTags.group[0]?.edges.forEach(tag => {
    // eslint-disable-next-line no-prototype-builtins
    if (tags.hasOwnProperty(tag.node?.name)) {
      ++tags[tag.node.name].count;
    } else {
      tags[tag.node.name] = {
        ...tag.node,
        count: 1
      };
    }
  });
  const sortedFilteredTags = Object.values(tags).filter(tag => tag.count >= minimumRelatedBlog).sort((a, b) => a.count <= b.count ? 1 : -1).slice(0, shownTagsCount);
  const rebuildIndex = () => {
    const dataToSearch = new JsSearch.Search('title');
    if (searchOptions.selectedStrategy === 'All') {
      dataToSearch.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
    }
    if (searchOptions.selectedStrategy === 'Prefix match') {
      dataToSearch.indexStrategy = new JsSearch.PrefixIndexStrategy();
    }
    dataToSearch.sanitizer = new JsSearch.LowerCaseSanitizer();
    dataToSearch.addIndex('title');
    dataToSearch.addIndex('field_lead_text');
    dataToSearch.addIndex('field_body_text');
    dataToSearch.addDocuments(normalizeNodes(blog.nodes));
    setIsLoading(false);
    setSearchEngine(dataToSearch);
  };
  const handleSubmit = event => {
    event.preventDefault();
  };
  const searchData = searchValue => {
    setSearchTerm(searchValue);
    const result = searchEngine.search(searchValue);
    setSearchResults(result.sort((a, b) => a.created <= b.created ? 1 : -1));
  };
  const normalizeNodes = nodes => {
    nodes.forEach(node => {
      const body = _get(node, 'relationships.field_body', []);
      let bodyText = '';
      body.forEach(item => {
        if (item?.internal?.type === 'paragraph__text') {
          bodyText += _get(item, 'field_text.value', '');
        } else if (item?.internal?.type === 'paragraph__heading_3') {
          bodyText += _get(item, 'field_heading_3', '');
        }
      });
      node.field_body_text = bodyText;
    });
    return nodes;
  };
  const onPaginationChange = (e, page) => {
    setActivePage(page || 1);
    setIsFirstPage(page === 1);
    setIsLastPage(page === totalPages);
    window?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const renderPagination = () => {
    const isPaginationNeeded = searchResults.length > searchOptions.itemPerPage;
    const totalPages = Math.ceil(searchResults.length / searchOptions.itemPerPage);
    return isPaginationNeeded ? <Pagination className="list-paginator" variant="outlined" shape="rounded" page={activePage || 1} onChange={onPaginationChange} count={totalPages} size="large" boundaryCount={0} siblingCount={1} showFirstButton={!isFirstPage} showLastButton={!isLastPage} hideNextButton={isLastPage} hidePrevButton={isFirstPage} renderItem={item => <PaginationItem components={{
      first: FirstPageIcon,
      last: LastPageIcon
    }} {...item} />} key={uniqueId()} /> : '';
  };
  const renderSearchResults = () => {
    const isPaginationNeeded = searchResults.length > searchOptions.itemPerPage;
    let results = [];
    if (isPaginationNeeded) {
      const offset = (activePage - 1) * searchOptions.itemPerPage;
      results = searchResults.slice(offset, offset + searchOptions.itemPerPage);
    } else {
      results = searchResults;
    }
    const resultList = [<div className="blog-search-result" key={uniqueId()}>
                {translate('search', langcode).result}
            </div>];
    resultList.push(results.map(result => <BlogListItem item={result} pathAlias={result?.path?.alias} key={uniqueId()} />));
    resultList.push(renderPagination());
    return resultList;
  };
  const renderLoading = () => <>
            <span className="loading-text">
                {translate('blog', langcode).loading}
            </span>
            <div id="lh-loading" className="bullets-3">
                <div id="lh-loading_1" className="lh-loading"></div>
                <div id="lh-loading_2" className="lh-loading"></div>
                <div id="lh-loading_3" className="lh-loading"></div>
                <div id="lh-loading_4" className="lh-loading"></div>
                <div id="lh-loading_5" className="lh-loading"></div>
                <div id="lh-loading_6" className="lh-loading"></div>
            </div>
        </>;
  const renderNoResult = () => {
    const searchTranslate = translate('search', langcode);
    return <div>
                <div className="search-no-result-title">
                    {searchTranslate.noResultTitle}
                </div>
                <ul>
                    {searchTranslate.noResultBody.map((item, index) => <li className="search-no-result-body" key={index}>
                            {item}
                        </li>)}
                </ul>
            </div>;
  };
  const renderResults = () => {
    let results = <React.Fragment />;
    if (searchTerm && searchResults.length !== 0) {
      results = renderSearchResults();
    } else if (searchTerm) {
      results = renderNoResult();
    }
    return results;
  };
  useEffect(() => {
    rebuildIndex();
  }, [rebuildIndex]);
  useEffect(() => {
    if (!totalPages) {
      setTotalPages(Math.ceil(searchResults.length / searchOptions.itemPerPage));
    }
    if (searchEngine) {
      searchData(searchTerm);
    }
  }, [searchTerm, searchEngine, searchData, searchOptions.itemPerPage, searchResults.length]);
  return <>
            {site !== 'hu' && <>
                    <div className="redesign-reset" style={{
        position: 'fixed',
        width: '100%',
        backgroundColor: 'white',
        zIndex: 10000
      }}>
                        <div className="content-grid">
                            <Slice alias="header" allowEmpty={true} />
                        </div>
                    </div>
                    <div className="layout-spacer-top"></div>
                </>}

            <App>
                <PageContextProvider langcode={langcode} pageData={pageData} pageContext="default">
                    {site === 'hu' && <Header pathAlias="/blog/search" langcode={langcode} />}

                    <div style={{
          width: '100%'
        }}>
                        <table width="100%" cellSpacing="0">
                            <tbody>
                                <tr>
                                    <td className="aside"></td>
                                    <td className="center-wrapper">
                                        <div style={{
                    width: '100%'
                  }} className="content blog">
                                            <BackNavigationLink to={prefixPath('/blogs', langcode)} label={translate('corporateBlog', langcode)} />

                                            <h1 className="content-title">
                                                {translate('blog', langcode).title}
                                            </h1>
                                            <div className="content-inner-wrapper">
                                                <div className="content-body-wrapper">
                                                    <form onSubmit={handleSubmit} className="blog-search-filter">
                                                        <input type="search" className="search-field" onChange={event => searchData(event.target.value)} value={searchTerm} placeholder={translate('blog', langcode).searchTerm} />
                                                    </form>
                                                    {isLoading ? renderLoading() : renderResults()}
                                                </div>
                                                <div className="content-body-additionals">
                                                    <CategoryList blogCategories={blogCategories.nodes} />
                                                    <BlogAuthorList blogAuthors={listedUsers} langcode={langcode} />
                                                    <PopularTags blogTags={sortedFilteredTags} langcode={langcode} />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="aside"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {site === 'hu' && <Footer langcode={langcode} />}
                </PageContextProvider>
            </App>

            {site !== 'hu' && <div className="redesign-reset">
                    <div className="content-grid">
                        <Slice alias="footer" allowEmpty={true} />
                    </div>
                </div>}
        </>;
};
export default BlogSearch;
export const Head = ({
  pageContext
}) => {
  const [siteUrl, setSiteUrl] = useState('/');
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setSiteUrl(document.location.host);
    }
  }, []);
  return <SEO pageData={{
    title: 'Blog Search',
    url: `${siteUrl}/blog/search`,
    metatag: [{
      tag: 'meta',
      attributes: {
        name: 'robots',
        content: 'noindex'
      }
    }]
  }} type="website" pageContext={pageContext} />;
};
const pageQuery = "1076229082";
BlogSearch.propTypes = {
  data: PropTypes.objectOf(PropTypes.shape(PropTypes.any)),
  location: PropTypes.shape(PropTypes.any),
  pageContext: PropTypes.shape(pageContextType).isRequired
};